import type { PhaseContributorTrackingReport } from "./types.ts";

export type PhaseContributorSortKey = "hours" | "commercialRate" | "internalRate" | "commercialBurn" | "internalCost";

/** The report's burn is calculated from these same rounded hours and the project rate. */
export function phaseContributorCommercialRate(item: PhaseContributorTrackingReport): number | null {
  if (item.commercialRate !== undefined) return item.commercialRate;
  if (item.commercialRateConfigured === false || item.commercialBurn == null) return null;
  const hours = Number(item.hours);
  const rate = Number(item.commercialBurn) / hours;
  return hours > 0 && Number.isFinite(rate) ? rate : null;
}

/** Older reports only include the total internal cost, so derive the hourly rate when possible. */
export function phaseContributorInternalRate(item: PhaseContributorTrackingReport): number | null {
  if (item.internalRate !== undefined) return item.internalRate;
  if (item.internalRateConfigured === false || item.internalCost == null) return null;
  const hours = Number(item.hours);
  const rate = Number(item.internalCost) / hours;
  return hours > 0 && Number.isFinite(rate) ? rate : null;
}

function displayOrder(item: PhaseContributorTrackingReport): number {
  const category = item.memberCategory === "ACTIVE" ? 0 : item.memberCategory === "SHADOW" ? 1 : 2;
  return category * 2 + (phaseContributorCommercialRate(item) == null ? 1 : 0);
}

export function sortPhaseContributors(
  rows: PhaseContributorTrackingReport[],
  sortKey: PhaseContributorSortKey,
  direction: "asc" | "desc",
): PhaseContributorTrackingReport[] {
  return [...rows].sort((left, right) => {
    const rank = displayOrder(left) - displayOrder(right);
    if (rank) return rank;
    const leftValue = sortKey === "commercialRate" ? phaseContributorCommercialRate(left) : sortKey === "internalRate" ? phaseContributorInternalRate(left) : left[sortKey];
    const rightValue = sortKey === "commercialRate" ? phaseContributorCommercialRate(right) : sortKey === "internalRate" ? phaseContributorInternalRate(right) : right[sortKey];
    const difference = Number(leftValue ?? 0) - Number(rightValue ?? 0);
    return (direction === "desc" ? -difference : difference) ||
      left.employeeName.localeCompare(right.employeeName) || left.employeeId - right.employeeId;
  });
}
